import React from "react";
import { action, observable } from "mobx";
import debug from "debug";
import { Store } from "@store";
import { FileUploadConfig } from "@bonliva-crm/shared";

const log = debug("app:store:UploadStore");

export type RequestUploadOptions = {
  acceptMimeTypes?: string;
  multiple?: boolean;
  maxSize?: number;
};

export class UploadStore {
  inputRef = React.createRef<HTMLInputElement>();

  @observable showImageCropperDialog = false;
  @observable imageCropperDialogFileUrl: string | null = null;

  config: FileUploadConfig = new FileUploadConfig();
  imagesConfig = new FileUploadConfig("images");

  private _uploadDialogCb: () => void = null;
  private _imageCropperDialogCb: (file?: File | null, croppedFileURL?: string) => void =
    null;

  constructor(public readonly rootStore: Store) {}

  requestFileUpload = (options: RequestUploadOptions = {}): Promise<File[]> => {
    return new Promise<File[]>((resolve, reject) => {
      this._uploadDialogCb = () => {
        const files: File[] = [];

        for (let i = 0; i < this.inputRef.current.files.length; i++) {
          const file = this.inputRef.current.files[i];

          if (options.maxSize && file.size > options.maxSize) {
            continue;
          }

          files.push(file);
        }

        resolve(files);
      };

      this.requestUpload(options);
    });
  };

  requestImageUploadAsUrl = (): Promise<string> => {
    return this.requestFileUpload({
      acceptMimeTypes: this.imagesConfig.acceptString,
      multiple: false
    }).then((files) => {
      if (!files && !files.length) {
        return null;
      }

      return fileToUrl(files[0]);
    });
  };

  handleUpload = () => {
    log("handleUpload");
    if (!this._uploadDialogCb) {
      console.warn("missing upload callback");
      return;
    }

    if (!this.inputRef.current.files) {
      log("upload cancelled");
      this._uploadDialogCb = null;
    }
    this._uploadDialogCb();
    this._uploadDialogCb = null;
    this.inputRef.current.value = "";
  };

  requestImageCrop = (fileURL: string) => {
    return new Promise<File>((resolve, reject) => {
      this.showImageCropperDialog = true;
      this.imageCropperDialogFileUrl = fileURL;

      this._imageCropperDialogCb = (file: File) => {
        this.showImageCropperDialog = false;
        resolve(file);
      };
    });
  };

  requestImageCropWithPreview = (fileURL: string) => {
    return new Promise<{ file: File; croppedFileURL: string }>((resolve, reject) => {
      this.showImageCropperDialog = true;
      this.imageCropperDialogFileUrl = fileURL;

      this._imageCropperDialogCb = (file: File, croppedFileURL: string) => {
        this.showImageCropperDialog = false;
        resolve({ file, croppedFileURL });
      };
    });
  };

  @action imageCropperDialogSubmit = (croppedFileURL: string) => {
    if (!this._imageCropperDialogCb) {
      console.warn("missing upload callback");
      return;
    }

    if (!croppedFileURL) {
      log("upload cancelled");
      this._imageCropperDialogCb();
      this._imageCropperDialogCb = null;
    } else {
      srcToFile(croppedFileURL, "profile.png", this.imagesConfig.acceptString).then(
        (finalFile) => {
          this._imageCropperDialogCb(finalFile, croppedFileURL);
          this._imageCropperDialogCb = null;
        }
      );
    }
  };

  @action imageCropperDialogClose = () => {
    this.showImageCropperDialog = false;
    this._imageCropperDialogCb();
    this._imageCropperDialogCb = null;
    this.imageCropperDialogFileUrl = null;
  };

  private requestUpload = (options: RequestUploadOptions = {}) => {
    log("requestUpload", options);
    const { acceptMimeTypes, multiple } = options;

    this.inputRef.current.accept = acceptMimeTypes || this.config.acceptString;
    this.inputRef.current.multiple = Boolean(multiple);
    this.inputRef.current.classList.add("dnd-input"); // see ConsulantQuality.tsx
    this.inputRef.current.click();
  };
}

function srcToFile(src: RequestInfo, fileName: string, mimeType: string) {
  return fetch(src)
    .then((res) => {
      return res.arrayBuffer();
    })
    .then((buf) => {
      return new File([buf], fileName, { type: mimeType });
    });
}

/**
 * Converts File to blob url
 */
function fileToUrl(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);

    fr.onload = function () {
      resolve(this.result as string);
    };

    fr.onerror = (err) => reject(err);
  });
}
